import moment from 'moment';
import {
  LOCAL_DATETIME_FORMAT,
  UTC_DATETIME_FORMAT,
  UTC_DATETIME_SECONDS_FORMAT,
} from '../../../constants';

export const DATA_DIRECTIONS = {
  BOTH: '',
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
};

export const COMMON_DATA_FILTERS = {
  START: 'start',
  END: 'end',
  SORT: 'sort',
};

export const PORTAL_DATA_RANGE_DAYS = 10;

export type Range = { start: moment.Moment; end: moment.Moment };
type StringRange = { start: string; end: string };
type StringOrMoment = moment.Moment | string;

/**
 * Private helper that returns an object with "start" and "end" properties that are
 * formatted date strings representing the supplied filters, constrained appropriately
 * @param {string | moment} startValue - date string/moment representing start of range
 * @param {string | moment} endValue - date string/moment representing end of range
 * @param {string} format - moment.js date format to output in
 * @param {boolean} inUTC - whether or not to use UTC instead of local time
 * @return {{start: string, end: string}}
 */
function getDateRange(
  startValue: StringOrMoment | null,
  endValue: StringOrMoment | null,
  format: string,
  inUTC: boolean,
): { start: string; end: string } {
  // Use the URL validators to get properly constrained start/end values
  let end =
    endValue && moment(endValue).isValid() ? moment(endValue) : moment();
  let start =
    startValue && moment(startValue).isValid()
      ? moment(startValue)
      : moment().subtract(1, 'day');
  if (inUTC) {
    start.utc();
    end.utc();
  }
  return {
    start: start.format(format),
    end: end.format(format),
  };
}

/**
 * Returns an object with "start" and "end" properties that are string representations of
 * the start and end times evaluated from the supplied inputs in browser local time
 * @param start - start time
 * @param end - end time
 * @return {{start: moment, end: moment}}
 */
export const getUrlDateRangeLocal = (
  start: StringOrMoment | null,
  end: StringOrMoment | null,
): StringRange => {
  return getDateRange(start, end, LOCAL_DATETIME_FORMAT, false);
};

/**
 * Returns an object with "start" and "end" properties that are ISO8601 UTC string representations
 * without seconds of the start and end times from the supplied inputs used for sharable absolute time URLs
 * @param start - start time
 * @param end - end time
 * @return {{start: moment, end: moment}}
 */
export const getURLDateRangeUTC = (
  start: StringOrMoment | null,
  end: StringOrMoment | null,
): StringRange => {
  return getDateRange(start, end, UTC_DATETIME_FORMAT, true);
};

/**
 * Returns an object with "start" and "end" properties that are ISO8601 UTC representations of
 * the start and end times in the format expected by the backend API (including seconds)
 * @param start - start time
 * @param end - end time
 * @return {{start: moment, end: moment}}
 */
export const getURLDateRangeAPI = (
  start: StringOrMoment | null,
  end: StringOrMoment | null,
): StringRange => {
  return getDateRange(start, end, UTC_DATETIME_SECONDS_FORMAT, true);
};

/**
 * Factory for returning a function that returns a local string representation of yesterday's date (with current time).
 */
export const dateYesterday = (): string => {
  return moment().subtract(1, 'day').format(LOCAL_DATETIME_FORMAT);
};

/**
 * Factory for returning a function that returns a local string representation of 'now' (today's date and time).
 */
export const dateNow = (): string => {
  return moment().format(LOCAL_DATETIME_FORMAT);
};
